interface PdfTextExtraction {
  pageCount: number
  text: string
  truncated: boolean
}

interface PdfTextContent {
  items: unknown[]
}

interface PdfPage {
  getTextContent: () => Promise<PdfTextContent>
}

interface PdfDocument {
  destroy?: () => void
  getPage: (pageNumber: number) => Promise<PdfPage>
  numPages: number
}

interface PdfLoadingTask {
  destroy?: () => void
  promise: Promise<PdfDocument>
}

interface PdfJsModule {
  getDocument: (source: { data: Uint8Array; disableWorker?: boolean }) => PdfLoadingTask
}

const MAX_PAGES_TO_READ = 12
const MAX_TEXT_LENGTH = 16_000

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function normalizeMergedText(value: string): string {
  return collapseWhitespace(
    value
      .split('\u0000')
      .join('')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
  )
}

function textFromItem(item: unknown): string {
  if (typeof item !== 'object' || item === null || !('str' in item)) {
    return ''
  }

  const next = (item as { str?: unknown }).str
  return typeof next === 'string' ? next : ''
}

async function loadPdfJsModule(): Promise<PdfJsModule> {
  try {
    const [pdfjs] = await Promise.all([
      import('pdfjs-dist/legacy/build/pdf') as Promise<unknown>,
      import('pdfjs-dist/legacy/build/pdf.worker.entry'),
    ])
    if (typeof pdfjs !== 'object' || pdfjs === null || !('getDocument' in pdfjs)) {
      throw new Error('PDF parser module failed to load.')
    }

    return pdfjs as PdfJsModule
  } catch {
    throw new Error('Failed to initialize PDF parser runtime.')
  }
}

export async function extractPdfTextFromFile(file: File): Promise<PdfTextExtraction> {
  const buffer = await file.arrayBuffer()
  const pdfjs = await loadPdfJsModule()
  const loadingTask = pdfjs.getDocument({
    data: new Uint8Array(buffer),
    disableWorker: true,
  })

  let pdfDocument: PdfDocument | null = null

  try {
    pdfDocument = await loadingTask.promise
    const pageCount = pdfDocument.numPages
    const pagesToRead = Math.min(pageCount, MAX_PAGES_TO_READ)

    const chunks: string[] = []
    let truncated = pageCount > MAX_PAGES_TO_READ

    for (let pageIndex = 1; pageIndex <= pagesToRead; pageIndex += 1) {
      const page = await pdfDocument.getPage(pageIndex)
      const textContent = await page.getTextContent()
      const pageText = normalizeMergedText(textContent.items.map(textFromItem).join(''))

      if (pageText.length > 0) {
        chunks.push(pageText)
      }

      if (chunks.join('\n\n').length >= MAX_TEXT_LENGTH) {
        truncated = true
        break
      }
    }

    let text = chunks.join('\n\n').trim()
    if (text.length > MAX_TEXT_LENGTH) {
      text = text.slice(0, MAX_TEXT_LENGTH)
      truncated = true
    }

    if (!text) {
      throw new Error('No extractable text found in the uploaded PDF. It may be an image-only scan.')
    }

    return {
      pageCount,
      text,
      truncated,
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }

    throw new Error('Failed to parse PDF content.')
  } finally {
    loadingTask.destroy?.()
    pdfDocument?.destroy?.()
  }
}
